import { checkAndSendScheduledEmails } from './utils/emailService';
import React, { useEffect, useState } from 'react';
import { AppProvider, useAppContext } from './context/AppContext';
import PrivacyPage from './PrivacyPage';
import TermsPage from './TermsPage';
import Dashboard from './modules/feasibility/Dashboard12';
import Dashboard34 from './modules/construction/Dashboard34';
import ModuleSelector from './components/ModuleSelector';
import AuthModal from './components/Modals/AuthModal';
import ProjectModal from './components/Modals/ProjectModal';
import PaymentModal from './modules/feasibility/PaymentModal12';
import RevisionModal from './modules/feasibility/RevisionModal12';
import RevisionPaymentModal from './modules/feasibility/RevisionPaymentModal12';
import RevisionRequestModal from './modules/feasibility/RevisionRequestModal12';
import UploadFormsModal from './components/Modals/UploadFormsModal';
import AdminPanel from './components/AdminPanel';
import Notifications from './components/Notifications';
import LandingPage from './components/LandingPage';
import TestingHelpers from './components/TestingHelpers';
import { db } from './config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { updatePaymentStatus } from './utils/supabase';

// FEATURE FLAG - Set to true when Stage 3 & 4 are ready to launch
const STAGE_3_4_ENABLED = false;

// MAINTENANCE MODE - Set to true during updates, admin users can still access
const MAINTENANCE_MODE = false;

// Maintenance Page Component
const MaintenancePage = ({ onLogin }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-700">
    <div className="text-center p-8 bg-white rounded-xl shadow-2xl max-w-md mx-4">
      <div className="text-6xl mb-4">🚧</div>
      <h1 className="text-2xl font-bold text-gray-800 mb-3">Under Maintenance</h1>
      <p className="text-gray-600 mb-6">
        We're updating our site with new features and improvements. 
        We'll be back shortly!
      </p>

      {onLogin && (
        <button
          onClick={onLogin}
          className="text-blue-600 hover:text-blue-800 text-sm underline"
        >
          Admin Login
        </button>
      )}
    </div>
  </div>
);

export default function PlanAIDesignApp() {
  // Add Google Ads tracking
  useEffect(() => {
    // Create script tag for gtag
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-1755678019';
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'AW-17556788019');
    
    // Make gtag available globally
    window.gtag = gtag;
    
    console.log('Google Ads tracking initialized');
  }, []);

  // Check URL path FIRST, before AppProvider
  const path = window.location.pathname;
  console.log('Current path:', path);
  
  if (path === '/privacy' || path.includes('privacy')) {
    console.log('Showing Privacy Page');
    return <PrivacyPage />;
  }
  
  if (path === '/terms' || path.includes('terms')) {
    console.log('Showing Terms Page');
    return <TermsPage />;
  }
  
  console.log('Showing normal app');
  // Normal app flow
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

function AppContent() {
  const {
    currentUser,
    showLanding,
    showAuthModal,
    setShowAuthModal,
    showProjectModal,
    showPaymentModal,
    showRevisionModal,
    showRevisionPaymentModal,
    showRevisionRequestModal,
    showUploadFormsModal,
    showNotification,
    setShowLanding,
    projects,
    setProjects,
    isAdminUser,
    showAdminPanel,
    setShowAdminPanel
  } = useAppContext();

  // Check for scheduled follow-up emails
  useEffect(() => {
    // Check for scheduled emails on app load and every hour
    const checkEmails = async () => {
      console.log('Checking for scheduled emails...');
      await checkAndSendScheduledEmails();
    };
    
    // Initial check
    checkEmails();
    
    // Check every hour (3600000 ms)
    const interval = setInterval(checkEmails, 3600000);
    
    return () => clearInterval(interval);
  }, []);

  // Force feasibility mode when Stage 3 & 4 are disabled
  const [appMode, setAppMode] = useState('feasibility');

  // Check for payment return with ENHANCED RETRY LOGIC
  useEffect(() => {
    // Prevent duplicate processing
    let isProcessing = false;
    
    const checkForPayment = async (retryCount = 0) => {
      // If already processing, skip
      if (isProcessing) return;
      
      const urlParams = new URLSearchParams(window.location.search);
      let paymentStatus = urlParams.get('payment');
      
      // ALSO check for ?payment-success format (Stripe return URL format)
      if (!paymentStatus && window.location.search.includes('payment-success')) {
        paymentStatus = 'success';
        console.log('Detected payment-success in URL (alternate format)');
      }
      
      if (!paymentStatus) {
        return; // No payment to process
      }
      
      // Mark as processing to prevent duplicates
      isProcessing = true;
      
      console.log('=== PAYMENT DETECTED (Attempt ' + (retryCount + 1) + ') ===');
      console.log('Status:', paymentStatus);
      
      // Get stored payment info (regular payments) with debugging
      const projectId = localStorage.getItem('pendingPaymentProjectId');
      const stage = localStorage.getItem('pendingPaymentStage');
      const paymentNumber = localStorage.getItem('pendingPaymentNumber');
      const paymentType = localStorage.getItem('pendingPaymentType'); // NEW: 'invoice' for Stage 1
      
      // Debug logging to see what's actually stored
      console.log('Raw localStorage values:', {
        projectId,
        stage,
        paymentNumber,
        paymentType,
        allKeys: Object.keys(localStorage).filter(k => k.includes('pending'))
      });
      
      // Get stored revision payment info
      const revisionProjectId = localStorage.getItem('pendingRevisionPaymentProjectId');
      const revisionPaymentType = localStorage.getItem('pendingRevisionPaymentType');
      const revisionPaymentStage = localStorage.getItem('pendingRevisionPaymentStage');
      const revisionPaymentFee = localStorage.getItem('pendingRevisionPaymentFee');
      const revisionDocType = localStorage.getItem('pendingRevisionPaymentDocType');
      const revisionFeedback = localStorage.getItem('pendingRevisionPaymentFeedback');
      const revisionNumber = localStorage.getItem('pendingRevisionPaymentNumber');
      const revisionFilesJson = localStorage.getItem('pendingRevisionPaymentFiles');
      const revisionFiles = revisionFilesJson ? JSON.parse(revisionFilesJson) : [];
      
      console.log('Stored info:', { projectId, stage, paymentNumber, paymentType });
      console.log('Revision info:', { revisionProjectId, revisionPaymentType, revisionFiles });
      
      // RETRY LOGIC: If no payment data found and this is a success status, retry
      if (paymentStatus === 'success' && !projectId && !revisionProjectId && retryCount < 5) {
        console.log('No payment data found, retrying in 1 second...');
        isProcessing = false; // Allow retry
        setTimeout(() => {
          checkForPayment(retryCount + 1);
        }, 1000);
        return;
      }
      
      // ADDITIONAL CHECK: Wait for Firebase to be ready and try multiple ID formats
      if (paymentStatus === 'success' && (revisionProjectId || projectId)) {
        // Wait a moment for Firebase to initialize
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check if project exists in Firebase
        const targetProjectId = revisionProjectId || projectId;
        let projectRef = doc(db, 'projects', targetProjectId);
        let projectSnap = null;
        
        try {
          projectSnap = await getDoc(projectRef);
          
          // If not found with direct ID, try alternative approaches
          if (!projectSnap.exists()) {
            console.log(`Project not found with ID: ${targetProjectId}, trying alternatives...`);
            
            // Try without any modifications
            const altRef = doc(db, 'projects', String(targetProjectId));
            const altSnap = await getDoc(altRef);
            
            if (altSnap.exists()) {
              projectRef = altRef;
              projectSnap = altSnap;
              console.log('Found project with string conversion');
            } else if (retryCount < 5) {
              console.log('Project not found in Firebase yet, retrying...');
              isProcessing = false; // Allow retry
              setTimeout(() => {
                checkForPayment(retryCount + 1);
              }, 1500);
              return;
            }
          }
        } catch (error) {
          console.error('Error checking Firebase:', error);
          if (retryCount < 5) {
            isProcessing = false; // Allow retry
            setTimeout(() => {
              checkForPayment(retryCount + 1);
            }, 1500);
            return;
          }
        }
      }
      
      if (paymentStatus === 'success' && revisionProjectId && revisionPaymentType === 'additional-revision') {
        // Process REVISION payment
        console.log('=== REVISION PAYMENT DETECTED ===');
        console.log('Revision payment for project:', revisionProjectId);
        
        const processRevisionPayment = async () => {
          try {
            // Add small delay to ensure Firebase is ready
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Get project from Firebase
            const projectRef = doc(db, 'projects', revisionProjectId);
            const projectSnap = await getDoc(projectRef);
            
            if (!projectSnap.exists()) {
              console.error('Project not found for revision payment');
              // Try one more time after delay
              if (retryCount < 3) {
                isProcessing = false; // Allow retry
                setTimeout(() => {
                  checkForPayment(retryCount + 1);
                }, 2000);
                return;
              }
              showNotification('Error', 'Project not found. Please contact support.', 'error');
              return;
            }
            
            const projectData = projectSnap.data();
            const revisionNum = parseInt(revisionNumber) || 1;
            const fee = parseInt(revisionPaymentFee) || 0;
            
            // CRITICAL FIX: Properly update revision tracking
            const updatedRevisions = {
              ...projectData.revisions,
              currentRound: Math.max(revisionNum, projectData.revisions?.currentRound || 0),
              totalRevisionsUsed: Math.max((projectData.revisions?.totalRevisionsUsed || 0) + 1, revisionNum)
            };
            
            // Update project with revision payment and request
            await setDoc(projectRef, {
              status: `revision-${revisionNum}-requested`,
              [`revision${revisionNum}PaymentStatus`]: 'paid',
              hasUsedIncludedRevision: true, // Set the flag for revision tracking
              revisions: updatedRevisions,
              // Add revision documents (files were uploaded before Stripe redirect)
              revisionDocuments: [
                ...(projectData.revisionDocuments || []),
                {
                  revisionNumber: revisionNum,
                  requirements: revisionFeedback,
                  files: revisionFiles, // URLs from Firebase Storage
                  uploadDate: new Date().toISOString(),
                  paymentConfirmed: true
                }
              ],
              communications: [
                ...(projectData.communications || []),
                {
                  type: 'system',
                  sender: 'planAIdesign System',
                  message: `Payment of $${fee} received for Revision ${revisionNum}.`,
                  timestamp: new Date().toLocaleString('en-AU', {
                    timeZone: 'Australia/Sydney',
                    year: 'numeric',
                    month: '2-digit', 
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }).replace(',', '').replace(/\//g, '-')
                },
                {
                  type: 'client',
                  sender: projectData.client,
                  message: `Revision ${revisionNum} requested: ${revisionFeedback}${revisionFiles.length > 0 ? ` (${revisionFiles.length} supporting file(s) attached)` : ''}`,
                  timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
                },
                {
                  type: 'architect',
                  sender: 'planAIdesign',
                  message: `We have received your revision request (payment confirmed). We will begin working on it and provide an update shortly.`,
                  timestamp: new Date(Date.now() + 2000).toISOString().slice(0, 16).replace('T', ' ')
                }
              ]
            }, { merge: true });
            
            console.log('Firebase updated for revision payment');
            
            // Update local state if projects are loaded
            if (projects && projects.length > 0) {
              setProjects(prev => prev.map(p => {
                if (p.id === revisionProjectId || p.firebaseId === revisionProjectId) {
                  return {
                    ...p,
                    status: `revision-${revisionNum}-requested`,
                    [`revision${revisionNum}PaymentStatus`]: 'paid',
                    hasUsedIncludedRevision: true,
                    revisions: updatedRevisions,
                    revisionDocuments: [
                      ...(p.revisionDocuments || []),
                      {
                        revisionNumber: revisionNum,
                        requirements: revisionFeedback,
                        files: revisionFiles,
                        uploadDate: new Date().toISOString(),
                        paymentConfirmed: true
                      }
                    ]
                  };
                }
                return p;
              }));
            }
            
            showNotification(
              'Revision Payment Successful!', 
              `Payment of $${fee} received. Your revision request has been submitted.`, 
              'success'
            );
            
          } catch (error) {
            console.error('Error processing revision payment:', error);
            // Retry if this was a temporary error
            if (retryCount < 3) {
              isProcessing = false; // Allow retry
              setTimeout(() => {
                checkForPayment(retryCount + 1);
              }, 2000);
              return;
            }
            showNotification('Revision Payment Error', 'Payment received but failed to update. Please refresh.', 'error');
          } finally {
            // Clean up revision payment localStorage
            localStorage.removeItem('pendingRevisionPaymentProjectId');
            localStorage.removeItem('pendingRevisionPaymentType');
            localStorage.removeItem('pendingRevisionPaymentStage');
            localStorage.removeItem('pendingRevisionPaymentFee');
            localStorage.removeItem('pendingRevisionPaymentDocType');
            localStorage.removeItem('pendingRevisionPaymentFeedback');
            localStorage.removeItem('pendingRevisionPaymentNumber');
            localStorage.removeItem('pendingRevisionPaymentFiles');
            
            // Clean URL
            window.history.replaceState({}, document.title, '/dashboard');
          }
        };
        
        processRevisionPayment();
        
      } else if (paymentStatus === 'success' && projectId) {
        // Process REGULAR payment with enhanced ID checking
        const processPayment = async () => {
          try {
            console.log('Processing payment for project:', projectId);
            
            // Add small delay to ensure Firebase is ready
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Get project from Firebase with fallback
            let projectRef = doc(db, 'projects', projectId);
            let projectSnap = await getDoc(projectRef);
            
            // If not found, try alternative ID format
            if (!projectSnap.exists()) {
              console.log(`Project not found with ID: ${projectId}, trying string conversion...`);
              projectRef = doc(db, 'projects', String(projectId));
              projectSnap = await getDoc(projectRef);
            }
            
            if (!projectSnap.exists()) {
              console.error('Project not found in Firebase after multiple attempts');
              // Retry if this is temporary
              if (retryCount < 3) {
                isProcessing = false; // Allow retry
                setTimeout(() => {
                  checkForPayment(retryCount + 1);
                }, 2000);
                return;
              }
              showNotification('Error', 'Project not found. Please contact support.', 'error');
              return;
            }
            
            const projectData = projectSnap.data();
            console.log('Current project data:', projectData);
            console.log('Payment type:', paymentType);
            console.log('Stage:', stage);
            
            let newPaymentsMade = 0;
            let newStatus = '';
            let newPaymentStatus = '';
            let paymentAmount = 0;
            let paymentMessage = '';
            let workMessage = '';
            
            // ============================================
            // STAGE 1 - NEW SINGLE PAYMENT MODEL
            // ============================================
            if (stage === '1') {
              // Stage 1 now uses single payment on delivery
              const price = projectData.paymentStructure?.paymentAmounts?.[0] || 199;
              
              newPaymentsMade = 1; // Single payment
              newStatus = 'paid';
              newPaymentStatus = 'paid';
              paymentAmount = price;
              
              paymentMessage = `Payment of $${price} received. Thank you for your payment.`;
              workMessage = 'Your final unwatermarked feasibility report is now available for download.';
              
              // Update Firebase with Stage 1 completion
              await setDoc(projectRef, {
                paymentsMade: 1,
                status: 'paid',
                paymentStatus: 'paid',
                paidAt: new Date().toISOString(),
                lastPaymentSync: new Date().toISOString(),
                paymentHistory: [
                  ...(projectData.paymentHistory || []),
                  {
                    amount: price,
                    date: new Date().toISOString(),
                    type: 'invoice-payment',
                    method: 'stripe-payment-link',
                    paymentNumber: 1
                  }
                ],
                communications: [
                  ...(projectData.communications || []),
                  {
                    type: 'system',
                    sender: 'planAIdesign System',
                    message: paymentMessage,
                    timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
                  },
                  {
                    type: 'architect',
                    sender: 'planAIdesign',
                    message: workMessage,
                    timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
                  }
                ]
              }, { merge: true });
              
              console.log('Firebase updated successfully for Stage 1 payment!');
              
              // Update local state
              if (projects && projects.length > 0) {
                setProjects(prev => prev.map(p => {
                  if (p.id === projectId || p.firebaseId === projectId || String(p.id) === String(projectId)) {
                    return {
                      ...p,
                      paymentsMade: 1,
                      status: 'paid',
                      paymentStatus: 'paid',
                      paidAt: new Date().toISOString(),
                      communications: [
                        ...(p.communications || []),
                        {
                          type: 'system',
                          sender: 'planAIdesign System',
                          message: paymentMessage,
                          timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
                        },
                        {
                          type: 'architect',
                          sender: 'planAIdesign',
                          message: workMessage,
                          timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
                        }
                      ]
                    };
                  }
                  return p;
                }));
              }
              
              // Show success notification
              showNotification(
                'Payment Successful!',
                'Your payment has been received. Your final report is now available for download.',
                'success'
              );
              
            // ============================================
            // STAGE 2 - EXISTING MULTI-PAYMENT MODEL
            // ============================================
            } else if (stage === '2') {
              const currentPayments = projectData.paymentsMade || 0;
              newPaymentsMade = currentPayments + 1;
              const totalPayments = projectData.paymentStructure?.totalPayments || 2;
              
              paymentAmount = projectData.paymentStructure?.paymentAmounts?.[currentPayments] || 150;
              
              if (newPaymentsMade >= totalPayments) {
                newStatus = 'paid';
                newPaymentStatus = 'paid';
              } else if (newPaymentsMade === totalPayments - 1 && totalPayments > 2) {
                newStatus = 'awaiting-final-payment';
                newPaymentStatus = 'partial';
              } else {
                newStatus = 'in-progress';
                newPaymentStatus = 'partial';
              }
              
              // Prepare messages for Stage 2
              if (newPaymentsMade === 1) {
                paymentMessage = `Payment ${newPaymentsMade} of ${totalPayments} received: $${paymentAmount}. Thank you for your payment.`;
                workMessage = 'Work on your Concept Design will begin shortly. We will notify you of progress updates.';
              } else if (newPaymentsMade === 2 && totalPayments === 3) {
                paymentMessage = `Payment ${newPaymentsMade} of ${totalPayments} received: $${paymentAmount}. Thank you for your payment.`;
                workMessage = 'Preliminary concept design will be uploaded shortly.';
              } else if (newPaymentsMade >= totalPayments) {
                paymentMessage = `Final payment (${newPaymentsMade} of ${totalPayments}) received: $${paymentAmount}. All payments complete.`;
                workMessage = 'Your concept design documentation will be finalized and delivered shortly.';
              } else {
                paymentMessage = `Payment ${newPaymentsMade} of ${totalPayments} received: $${paymentAmount}. Thank you for your payment.`;
                workMessage = 'Work continues on your concept design. Next payment will be required for final documentation.';
              }
              
              console.log('Updating Stage 2 to:', { newPaymentsMade, newStatus, newPaymentStatus });
              
              // Update Supabase first (direct connection, no CORS issues)
              try {
                console.log('Updating Supabase...');
                const supabaseResult = await updatePaymentStatus(
                  projectId,
                  newPaymentsMade,
                  newStatus,
                  newPaymentStatus
                );
                
                if (supabaseResult.success) {
                  console.log('Supabase updated successfully');
                } else {
                  console.error('Supabase update failed:', supabaseResult.error);
                }
              } catch (supabaseError) {
                console.error('Supabase error (non-critical):', supabaseError);
              }
              
              // Update Firebase (primary database)
              await setDoc(projectRef, {
                paymentsMade: newPaymentsMade,
                status: newStatus,
                paymentStatus: newPaymentStatus,
                lastPaymentSync: new Date().toISOString(),
                paymentHistory: [
                  ...(projectData.paymentHistory || []),
                  {
                    amount: paymentAmount,
                    date: new Date().toISOString(),
                    type: currentPayments === 0 ? 'initial' : 
                          currentPayments === (totalPayments - 1) ? 'final' : 'progress',
                    method: 'stripe',
                    paymentNumber: newPaymentsMade
                  }
                ],
                communications: [
                  ...(projectData.communications || []),
                  {
                    type: 'system',
                    sender: 'planAIdesign System',
                    message: paymentMessage,
                    timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
                  },
                  {
                    type: 'architect',
                    sender: 'planAIdesign',
                    message: workMessage,
                    timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
                  }
                ]
              }, { merge: true });
              
              console.log('Firebase updated successfully for Stage 2 payment!');
              
              // Update local state
              if (projects && projects.length > 0) {
                setProjects(prev => prev.map(p => {
                  if (p.id === projectId || p.firebaseId === projectId || String(p.id) === String(projectId)) {
                    return {
                      ...p,
                      paymentsMade: newPaymentsMade,
                      status: newStatus,
                      paymentStatus: newPaymentStatus,
                      communications: [
                        ...(p.communications || []),
                        {
                          type: 'system',
                          sender: 'planAIdesign System',
                          message: paymentMessage,
                          timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
                        },
                        {
                          type: 'architect',
                          sender: 'planAIdesign',
                          message: workMessage,
                          timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
                        }
                      ]
                    };
                  }
                  return p;
                }));
              }
              
              // Show success notification
              showNotification(
                'Payment Successful!',
                `Payment ${newPaymentsMade} of ${totalPayments} received successfully.`,
                'success'
              );
            }
            
          } catch (error) {
            console.error('Error processing payment:', error);
            // Retry if this was temporary
            if (retryCount < 3) {
              isProcessing = false; // Allow retry
              setTimeout(() => {
                checkForPayment(retryCount + 1);
              }, 2000);
              return;
            }
            showNotification('Payment Error', 'Payment received but failed to update. Please refresh.', 'error');
          } finally {
            // Clean up regular payment localStorage
            localStorage.removeItem('pendingPaymentProjectId');
            localStorage.removeItem('pendingPaymentType');
            localStorage.removeItem('pendingPaymentStage');
            localStorage.removeItem('pendingPaymentNumber');
            localStorage.removeItem('pendingTotalPayments');
            
            // Clean URL
            window.history.replaceState({}, document.title, '/dashboard');
          }
        };
        
        // Process the payment
        processPayment();
        
      } else if (paymentStatus === 'cancelled') {
        console.log('Payment was cancelled');
        showNotification('Payment Cancelled', 'You can try again when ready.', 'info');
        
        // Clean up ALL payment localStorage items
        localStorage.removeItem('pendingPaymentProjectId');
        localStorage.removeItem('pendingPaymentType');
        localStorage.removeItem('pendingPaymentStage');
        localStorage.removeItem('pendingPaymentNumber');
        localStorage.removeItem('pendingTotalPayments');
        
        // Clean up revision payment localStorage
        localStorage.removeItem('pendingRevisionPaymentProjectId');
        localStorage.removeItem('pendingRevisionPaymentType');
        localStorage.removeItem('pendingRevisionPaymentStage');
        localStorage.removeItem('pendingRevisionPaymentFee');
        localStorage.removeItem('pendingRevisionPaymentDocType');
        localStorage.removeItem('pendingRevisionPaymentFeedback');
        localStorage.removeItem('pendingRevisionPaymentNumber');
        localStorage.removeItem('pendingRevisionPaymentFiles');
        
        // Clean URL
        window.history.replaceState({}, document.title, '/dashboard');
      }
      
      console.log('=== END PAYMENT DETECTION ===');
    };
    
    // Start the payment check
    checkForPayment();
  }, []); // Empty dependency array - runs once on mount

  // ADD WINDOW OBJECT TESTING HERE
  useEffect(() => {
    // Testing utilities for browser console
    window.testPaymentSync = {
      async updateProject(projectId, paymentsMade, status, paymentStatus) {
        console.log('Testing payment update:', { projectId, paymentsMade, status, paymentStatus });
        
        try {
          // Test Supabase update
          const supabaseResult = await updatePaymentStatus(projectId, paymentsMade, status, paymentStatus);
          console.log('Supabase result:', supabaseResult);
          
          // Test Firebase update  
          const projectRef = doc(db, 'projects', projectId);
          await setDoc(projectRef, {
            paymentsMade,
            status,
            paymentStatus,
            lastPaymentSync: new Date().toISOString()
          }, { merge: true });
          console.log('Firebase updated');
          
          // Update local state
          setProjects(prev => prev.map(p => {
            if (p.id === projectId) {
              return { ...p, paymentsMade, status, paymentStatus };
            }
            return p;
          }));
          
          showNotification('Test Success', 'Payment sync test completed', 'success');
          return { success: true };
          
        } catch (error) {
          console.error('Test failed:', error);
          showNotification('Test Failed', error.message, 'error');
          return { success: false, error: error.message };
        }
      },
      
      // ============================================
      // NEW: Stage 1 Card Capture Model Test Functions
      // ============================================
      
      // Test Stage 1 card capture status
      async testStage1CardCapture(projectId) {
        return this.updateProject(projectId, 0, 'in-progress', 'card-captured');
      },
      
      // Test Stage 1 invoice sent
      async testStage1InvoiceSent(projectId) {
        const projectRef = doc(db, 'projects', projectId);
        await setDoc(projectRef, {
          status: 'invoice-sent',
          paymentStatus: 'invoice-sent',
          invoiceSentAt: new Date().toISOString()
        }, { merge: true });
        
        setProjects(prev => prev.map(p => {
          if (p.id === projectId) {
            return { 
              ...p, 
              status: 'invoice-sent', 
              paymentStatus: 'invoice-sent',
              invoiceSentAt: new Date().toISOString()
            };
          }
          return p;
        }));
        
        console.log('Invoice sent status set for', projectId);
        showNotification('Test Success', 'Invoice sent status applied', 'success');
        return { success: true };
      },
      
      // Test Stage 1 payment complete
      async testStage1Paid(projectId) {
        return this.updateProject(projectId, 1, 'paid', 'paid');
      },
      
      // Test Stage 1 auto-charge failed
      async testStage1AutoChargeFailed(projectId) {
        const projectRef = doc(db, 'projects', projectId);
        await setDoc(projectRef, {
          status: 'invoice-sent',
          paymentStatus: 'auto-charge-failed',
          paymentError: 'Card declined - insufficient funds'
        }, { merge: true });
        
        setProjects(prev => prev.map(p => {
          if (p.id === projectId) {
            return { 
              ...p, 
              status: 'invoice-sent', 
              paymentStatus: 'auto-charge-failed' 
            };
          }
          return p;
        }));
        
        console.log('Auto-charge failed status set for', projectId);
        showNotification('Test Success', 'Auto-charge failed status applied', 'success');
        return { success: true };
      },
      
      // Stage 2 test functions (unchanged)
      async testStage2Payment(projectId) {
        return this.updateProject(projectId || '1755345229530', 1, 'in-progress', 'partial');
      },
      
      // Reset function
      async reset(projectId) {
        console.log('Resetting test data...');
        const id = projectId || '1755143944455';
        await this.updateProject(id, 0, 'awaiting-card-capture', 'pending');
        console.log('Reset complete for', id);
      }
    };
    
    console.log('Payment testing utilities loaded! Try:');
    console.log('window.testPaymentSync.testStage1CardCapture("PROJECT_ID")');
    console.log('window.testPaymentSync.testStage1InvoiceSent("PROJECT_ID")');
    console.log('window.testPaymentSync.testStage1Paid("PROJECT_ID")');
    console.log('window.testPaymentSync.testStage1AutoChargeFailed("PROJECT_ID")');
    console.log('window.testPaymentSync.testStage2Payment("PROJECT_ID")');
    console.log('window.testPaymentSync.reset("PROJECT_ID")');
  }, [setProjects, showNotification]);

  // MAINTENANCE MODE CHECK
  // - Non-logged-in users: Show maintenance page with login option
  // - Logged-in non-admin users: Show maintenance page
  // - Admin users: Full access
  if (MAINTENANCE_MODE) {
    // If not logged in, show maintenance with login option
    if (!currentUser) {
      return (
        <>
          <MaintenancePage onLogin={() => setShowAuthModal(true)} />
          <AuthModal />
        </>
      );
    }
    
    // If logged in but not admin, show maintenance (no login option needed)
    if (!isAdminUser()) {
      return <MaintenancePage />;
    }
    
    // Admin users continue to full app below
  }

  return (
    <>
      {/* Show landing page if not logged in */}
      {!currentUser && showLanding && <LandingPage />}
      
      {/* Show Admin Panel if admin user requests it - SECURITY FIXED */}
      {currentUser && !showLanding && showAdminPanel && isAdminUser() && (
        <AdminPanel />
      )}
      
      {/* Show dashboard WITHOUT module selector - Stage 1 & 2 only */}
      {currentUser && !showLanding && !showAdminPanel && (
        <>
          {/* Module selector hidden when Stage 3 & 4 disabled */}
          {STAGE_3_4_ENABLED && (
            <ModuleSelector currentMode={appMode} setMode={setAppMode} />
          )}
          
        {appMode === 'feasibility' ? <Dashboard /> : <Dashboard34 />}
        </>
      )}
      
      {/* Admin Panel Toggle Button - SECURITY FIXED */}
      {currentUser && !showLanding && isAdminUser() && (
        <div className="fixed bottom-4 right-4 z-40">
          <button
            onClick={() => {
              // Double-check admin status before allowing access
              if (!isAdminUser()) {
                showNotification('Access Denied', 'Admin privileges required', 'error');
                return;
              }
              console.log('Admin button clicked!');
              setShowAdminPanel(!showAdminPanel);
            }}
            className="px-4 py-2 rounded-lg text-white font-medium shadow-lg transition-colors bg-purple-600 hover:bg-purple-700"
          >
            {showAdminPanel ? 'Back to Dashboard' : 'Admin Panel'}
          </button>
        </div>
      )}
      
      {/* Testing Helpers - invisible component */}
      <TestingHelpers />
      
      {/* Modals */}
      <AuthModal />
      <ProjectModal />
      <PaymentModal />
      <RevisionModal />
      <RevisionPaymentModal />
      <RevisionRequestModal />
      <UploadFormsModal />
      <Notifications />
    </>
  );
}